/**
 * 🧠 DailyMindsetBanner — surfaces today's reflection question on the
 * Dashboard so EVERY user (new + existing) is greeted by a morning prompt.
 *
 * Yuri spec 2026-05-06: "Je veux qu'ils soient accueillis chaque matin par
 * une réflexion qui les force à définir leur intention du jour."
 *
 *  • Pre-answer  → orange-glow CTA "Définis ton intention" + question preview
 *  • Post-answer → soft "✓ Mindset noté" with 🔥streak chip
 *  • Click       → /mindset full page
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Flame, ChevronRight } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';

const ORANGE_GLOW = '0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45)';

export default function DailyMindsetBanner() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    let alive = true;
    // Prefetch triggers AI generation in the background (fire-and-forget on
    // the server) AND returns instantly with either the cached AI question
    // or the static fallback so the banner never blocks.
    Promise.all([
      api.post('/mindset/prefetch').catch(() => api.get('/mindset/today').catch(() => ({ data: null }))),
      api.get('/mindset/history?days=7').catch(() => ({ data: { streak_days: 0 } })),
    ]).then(([t, h]) => {
      if (!alive) return;
      // /prefetch returns {ready, question, date} — adapt to the same shape /today returns
      const payload = t?.data || {};
      setData({
        question: payload.question,
        answered: false,                    // banner doesn't need this — page will reload
        is_personalized: !!payload.ready,
        date: payload.date,
        _ready: payload.ready !== false,
      });
      setStreak(h.data?.streak_days || 0);

      // If AI was not yet ready, refetch in 4s so the personalized question
      // replaces the static one once the background generation completes.
      if (payload.ready === false) {
        setTimeout(() => {
          api.get('/mindset/today').then((res) => {
            if (!alive || !res?.data) return;
            setData((prev) => ({
              ...(prev || {}),
              question: res.data.question,
              is_personalized: !!res.data.is_personalized,
              answered: !!res.data.answered,
            }));
          }).catch(() => {});
        }, 4000);
      } else {
        // Even when prefetch reports cached, also pull /today once so we
        // know the answered state.
        api.get('/mindset/today').then((res) => {
          if (!alive || !res?.data) return;
          setData((prev) => ({
            ...(prev || {}),
            question: res.data.question,
            is_personalized: !!res.data.is_personalized,
            answered: !!res.data.answered,
          }));
        }).catch(() => {});
      }
    });
    return () => { alive = false; };
  }, []);

  if (!data?.question) return null;

  const answered = !!data.answered;
  const questionText = fr ? data.question.fr : data.question.en;

  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => navigate('/mindset')}
      className={`group w-full mb-4 flex items-stretch gap-3 rounded-xl border p-3 text-left transition-all hover:scale-[1.01] ${
        answered
          ? 'border-amber-400/30 bg-gradient-to-r from-amber-500/8 to-card'
          : 'border-amber-400/50 bg-gradient-to-r from-orange-500/15 via-card to-amber-500/10'
      }`}
      style={!answered ? { boxShadow: ORANGE_GLOW } : undefined}
      data-testid="daily-mindset-banner"
    >
      {/* Icon bubble */}
      <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
        answered ? 'bg-amber-500/15 ring-1 ring-amber-400/30' : 'bg-amber-500/20 ring-1 ring-amber-400/50'
      }`}>
        <Brain className="w-6 h-6 text-amber-300" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-[9px] uppercase tracking-[0.25em] font-bold text-amber-400">
            {fr ? 'Rituel Mindset' : 'Mindset Ritual'}
          </p>
          {data.is_personalized && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-orange-500/20 ring-1 ring-orange-400/50"
              data-testid="mindset-banner-ai-badge"
            >
              <span className="text-[8px] font-black uppercase tracking-widest text-orange-300 leading-none">
                {fr ? '✨ IA' : '✨ AI'}
              </span>
            </span>
          )}
          {streak >= 1 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 ring-1 ring-amber-400/40">
              <Flame className="w-2.5 h-2.5 text-amber-400" />
              <span className="text-[9px] font-black text-amber-300 leading-none">
                {streak}{fr ? 'j' : 'd'}
              </span>
            </span>
          )}
        </div>
        {answered ? (
          <>
            <p className="text-xs text-amber-300 font-bold uppercase tracking-widest">
              ✓ {fr ? 'Réflexion notée' : 'Reflection saved'}
            </p>
            <p className="text-[11px] text-muted-foreground truncate mt-0.5">
              {fr ? 'Tape ici pour relire ou modifier' : 'Tap here to review or edit'}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-heading text-foreground leading-snug line-clamp-2">
              {questionText}
            </p>
            <p className="text-[10px] mt-0.5 uppercase tracking-widest text-amber-400 font-bold">
              {fr ? '→ Définis ton intention' : '→ Set your intention'}
            </p>
          </>
        )}
      </div>

      <ChevronRight className="w-4 h-4 text-amber-300/70 self-center shrink-0" />
    </motion.button>
  );
}
